import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
// isSyncingRef: used to prevent onUpdate from triggering store updates when
// we're syncing content from external sources (e.g., preview mode)
import { useTranslation } from "react-i18next";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { Table } from "@tiptap/extension-table";
import {
  TableRowWithBackground,
  type RowBackgroundColor,
} from "@/canvas/extensions/tableRowBackground";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Markdown, MarkdownStorage } from "tiptap-markdown";
import Link from "@tiptap/extension-link";
import { mergeAttributes, posToDOMRect } from "@tiptap/core";
import { Bold, Italic, List, ListOrdered, CheckSquare, Paintbrush, Trash2 } from "lucide-react";
import { Block, MarkdownBlockContent } from "../../types";
import { useCanvasStore } from "../../store";
import { CitationNode } from "./CitationNode";
import { type BubbleMenuSide, inferBubbleMenuSide } from "./markdownBubbleMenuPlacement";
import { getMarkdownText } from "@/canvas/types/helpers";
import { cn } from "@/lib/utils";
import { buildTocHeadingId } from "../../utils/tocHeadingId";

/** Matches `{bg:danger}`, `{bg:warning}`, `{bg:success}` at start of cell text. */
const BG_MARKER_RE = /^\{bg:(danger|warning|success)\} ?/;

function getColorSwatches(
  t: (key: string) => string,
): { value: RowBackgroundColor; bg: string; label: string }[] {
  return [
    { value: null, bg: "transparent", label: t("canvas.blocks.markdown.rowColor.none") },
    { value: "danger", bg: "#ef4444", label: t("canvas.blocks.markdown.rowColor.danger") },
    { value: "warning", bg: "#f59e0b", label: t("canvas.blocks.markdown.rowColor.warning") },
    { value: "success", bg: "#22c55e", label: t("canvas.blocks.markdown.rowColor.success") },
  ];
}

// Extend TipTap storage type for markdown extension
interface EditorStorageWithMarkdown {
  markdown?: MarkdownStorage & {
    parser?: { parse: (content: string) => string };
  };
}

interface MarkdownBlockProps {
  block: Block & { content: MarkdownBlockContent };
  readOnly?: boolean;
}

type MarkdownTextStyle = "normal" | "heading1" | "heading2" | "heading3";

export function MarkdownBlock({ block, readOnly = false }: MarkdownBlockProps) {
  const { t, i18n } = useTranslation();
  const colorSwatches = useMemo(() => getColorSwatches(t), [t]);
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  // Ref to track when we're syncing external content to prevent autosave.
  // Starts TRUE so the initial onUpdate from editor creation is ignored.
  const isSyncingRef = useRef(true);
  const initialContentRef = useRef<string | null>(null);
  // Force re-render when editor state changes (for toolbar state)
  const [, forceUpdate] = useState({});
  const bubbleMenuRef = useRef<HTMLDivElement | null>(null);
  const [bubbleMenuSide, setBubbleMenuSide] = useState<BubbleMenuSide>("top");

  // Row color overlay state (for tables rendered inside the markdown editor)
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredTable, setHoveredTable] = useState<HTMLTableElement | null>(null);
  const [rowRect, setRowRect] = useState<{ top: number; height: number } | null>(null);
  const [tableLeft, setTableLeft] = useState(0);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [activeRowColor, setActiveRowColor] = useState<RowBackgroundColor>(null);
  const hoverClearTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Safely extract markdown text - handles both V2 format ({ text: string })
  // and malformed data where content might be a string directly
  const markdownText = getMarkdownText(block.content);
  const editorLanguage = i18n.resolvedLanguage ?? i18n.language ?? "sv";

  // Create custom Heading extension that adds data-heading-id attributes for scroll tracking
  // Memoized to prevent recreation on every render
  const CustomHeading = useMemo(
    () =>
      Heading.extend({
        renderHTML({ node, HTMLAttributes }) {
          const level = node.attrs.level as number;
          const text = node.textContent;
          const headingId = buildTocHeadingId(block.id, level, text);
          return [`h${level}`, { ...HTMLAttributes, "data-heading-id": headingId }, 0];
        },
      }),
    [block.id],
  );

  // Initialize TipTap editor with markdown extension
  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        // Disable StarterKit's heading, we use our custom one
        heading: false,
        // Disable StarterKit's link, we use our custom extended one below
        link: false,
      }),
      CustomHeading.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Table.configure({
        resizable: false,
        renderWrapper: true,
      }),
      TableRowWithBackground,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: t("canvas.blocks.markdown.placeholder"),
      }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      CitationNode,
      Link.extend({
        renderHTML({ HTMLAttributes }) {
          return [
            "a",
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
              class:
                "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-background border border-border text-muted-foreground no-underline cursor-pointer hover:bg-accent hover:text-foreground transition-colors",
            }),
            0,
          ];
        },
      }).configure({
        openOnClick: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: markdownText,
    editorProps: {
      attributes: {
        class: "text-ui-body focus:outline-none min-h-[100px]",
        lang: editorLanguage,
        spellcheck: "true",
      },
    },
    onCreate: () => {
      // Allow onUpdate to save after editor has fully initialized.
      // Use double-RAF so any transactions from the initial content parse
      // (which fire onUpdate synchronously) are ignored.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isSyncingRef.current = false;
        });
      });
    },
    onUpdate: ({ editor }) => {
      // Skip store update while syncing (initial load or external content sync).
      if (isSyncingRef.current) return;

      // Get markdown from the editor storage
      const storage = editor.storage as EditorStorageWithMarkdown;
      const markdown = storage.markdown?.getMarkdown() || "";
      updateBlockContent(block.id, {
        type: "markdown",
        text: markdown,
      });
    },
    onSelectionUpdate: () => {
      // Force re-render to update toolbar button states
      forceUpdate({});
    },
    onTransaction: () => {
      // Force re-render on any transaction for toolbar state updates
      forceUpdate({});
    },
  });

  // Sync content when block changes from external source
  // Sync external content changes (e.g., from AI agent updates) to the editor.
  // Note: We intentionally DON'T check for empty markdownText here - the agent
  // may clear a block, and we need to sync that empty state to the editor.
  useEffect(() => {
    if (!editor) return;

    // Store initial content on first render
    if (initialContentRef.current === null) {
      initialContentRef.current = markdownText;
      return;
    }

    // Only update if content changed externally (not from our own edits)
    const storage = editor.storage as EditorStorageWithMarkdown;
    const currentMarkdown = storage.markdown?.getMarkdown() || "";

    // Normalize both for comparison (trim whitespace)
    // Handle empty markdownText to allow agent to clear blocks
    if (currentMarkdown.trim() !== (markdownText || "").trim()) {
      // Set flag before sync to prevent onUpdate from triggering autosave.
      isSyncingRef.current = true;
      // Pass the markdown string directly to setContent — tiptap-markdown
      // intercepts setContent and parses it as markdown. Do NOT manually
      // parse to HTML first: that causes tiptap-markdown to re-parse the
      // HTML as markdown with html:false, entity-encoding all tags.
      editor.commands.setContent(markdownText || "");
      // Clear flag after TipTap has finished processing all transactions.
      // Use double-RAF to ensure we're past both the synchronous onUpdate
      // fired by setContent AND any async transactions TipTap may schedule.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isSyncingRef.current = false;
        });
      });
    }
  }, [markdownText, editor]);

  // Update editor editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // --- Table row color overlay ---

  const cancelHoverClear = useCallback(() => {
    if (hoverClearTimeout.current) {
      clearTimeout(hoverClearTimeout.current);
      hoverClearTimeout.current = null;
    }
  }, []);

  const scheduleHoverClear = useCallback(() => {
    cancelHoverClear();
    hoverClearTimeout.current = setTimeout(() => {
      setHoveredRow(null);
      setHoveredTable(null);
      setColorPickerOpen(false);
      hoverClearTimeout.current = null;
    }, 200);
  }, [cancelHoverClear]);

  useEffect(() => () => cancelHoverClear(), [cancelHoverClear]);

  /** Read the {bg:X} marker from the first cell's text at the given row index. */
  const readRowColorFromMarker = useCallback(
    (rowIndex: number, tableEl: HTMLTableElement): RowBackgroundColor => {
      if (!editor) return null;
      const rows = tableEl.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return null;
      const firstCell = targetRow.querySelector("td, th") as HTMLElement | null;
      if (!firstCell) return null;
      const cellPos = editor.view.posAtDOM(firstCell, 0);
      const $pos = editor.state.doc.resolve(cellPos);
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if (n.type.name === "tableRow") {
          const firstText = n.firstChild?.firstChild?.firstChild;
          if (!firstText || firstText.type.name !== "text") return null;
          const match = firstText.text?.match(BG_MARKER_RE);
          return match ? (match[1] as RowBackgroundColor) : null;
        }
      }
      return null;
    },
    [editor],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button")) {
        cancelHoverClear();
        return;
      }
      const cell = target.closest("td, th") as HTMLTableCellElement | null;
      if (!cell) {
        scheduleHoverClear();
        return;
      }
      cancelHoverClear();
      const tr = cell.closest("tr");
      if (!tr) return;
      const tableEl = tr.closest("table") as HTMLTableElement | null;
      if (!tableEl) return;
      const container = containerRef.current;
      if (!container) return;
      const rows = tableEl.querySelectorAll("tr");
      const rowIndex = Array.from(rows).indexOf(tr);
      if (rowIndex === hoveredRow && tableEl === hoveredTable) return;
      const containerRect = container.getBoundingClientRect();
      const trRect = tr.getBoundingClientRect();
      const tableWrapper = tableEl.closest(".tableWrapper") as HTMLElement | null;
      if (tableWrapper) {
        setTableLeft(tableWrapper.getBoundingClientRect().left - containerRect.left);
      }
      setHoveredRow(rowIndex);
      setHoveredTable(tableEl);
      setRowRect({ top: trRect.top - containerRect.top, height: trRect.height });
      setColorPickerOpen(false);
      setActiveRowColor(readRowColorFromMarker(rowIndex, tableEl));
    },
    [hoveredRow, hoveredTable, cancelHoverClear, scheduleHoverClear, readRowColorFromMarker],
  );

  const handleMouseLeave = useCallback(() => scheduleHoverClear(), [scheduleHoverClear]);

  /** Insert or replace the {bg:X} marker in the first cell of the hovered row. */
  const handleSetRowColor = useCallback(
    (rowIndex: number, tableEl: HTMLTableElement, color: RowBackgroundColor) => {
      if (!editor) return;
      const rows = tableEl.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return;
      const firstCell = targetRow.querySelector("td, th") as HTMLElement | null;
      if (!firstCell) return;
      const cellPos = editor.view.posAtDOM(firstCell, 0);
      const $pos = editor.state.doc.resolve(cellPos);
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if (n.type.name === "tableRow") {
          // Position of first text character:
          // $pos.start(d) = inside tableRow → +1 skips tableCell open → +1 skips para open
          const textStart = $pos.start(d) + 2;
          const firstText = n.firstChild?.firstChild?.firstChild;
          const match =
            firstText?.type.name === "text" ? firstText.text?.match(BG_MARKER_RE) : null;
          const { tr, schema } = editor.state;
          if (color === null) {
            if (match) tr.delete(textStart, textStart + match[0].length);
          } else {
            const markerNode = schema.text(`{bg:${color}} `);
            if (match) {
              tr.replaceWith(textStart, textStart + match[0].length, markerNode);
            } else {
              tr.insert(textStart, markerNode);
            }
          }
          editor.view.dispatch(tr);
          setActiveRowColor(color);
          setColorPickerOpen(false);
          return;
        }
      }
    },
    [editor],
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number, tableEl: HTMLTableElement) => {
      if (!editor) return;
      const rows = tableEl.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return;
      const firstCell = targetRow.querySelector("td, th");
      if (!firstCell) return;
      const pos = editor.view.posAtDOM(firstCell, 0);
      editor.chain().focus().setTextSelection(pos).deleteRow().run();
      setHoveredRow(null);
    },
    [editor],
  );

  // Toolbar actions with toggle behavior
  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
  }, [editor]);

  const toggleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
  }, [editor]);

  const toggleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const toggleTaskList = useCallback(() => {
    editor?.chain().focus().toggleTaskList().run();
  }, [editor]);

  const setTextStyle = useCallback(
    (style: MarkdownTextStyle) => {
      if (!editor) return;

      const chain = editor.chain().focus();

      switch (style) {
        case "normal":
          chain.setParagraph().run();
          break;
        case "heading1":
          chain.setHeading({ level: 1 }).run();
          break;
        case "heading2":
          chain.setHeading({ level: 2 }).run();
          break;
        case "heading3":
          chain.setHeading({ level: 3 }).run();
          break;
      }
    },
    [editor],
  );

  const currentTextStyle: MarkdownTextStyle = editor?.isActive("heading", { level: 1 })
    ? "heading1"
    : editor?.isActive("heading", { level: 2 })
      ? "heading2"
      : editor?.isActive("heading", { level: 3 })
        ? "heading3"
        : "normal";

  const shouldShowBubbleMenu = useCallback(
    ({
      editor,
      state,
      from,
      to,
    }: {
      editor: Editor;
      state: Editor["state"];
      from: number;
      to: number;
    }) => {
      if (readOnly || !editor.isEditable) return false;

      const hasSelection = !state.selection.empty && !!state.doc.textBetween(from, to).length;
      return editor.isFocused && hasSelection;
    },
    [readOnly],
  );

  const syncBubbleMenuSide = useCallback(() => {
    if (!editor || !bubbleMenuRef.current) return;

    const { selection } = editor.state;
    const selectionRect = posToDOMRect(editor.view, selection.from, selection.to);
    const menuRect = bubbleMenuRef.current.getBoundingClientRect();
    const nextSide = inferBubbleMenuSide(menuRect, selectionRect);

    setBubbleMenuSide((currentSide) => (currentSide === nextSide ? currentSide : nextSide));
  }, [editor]);

  const textStyleActions = [
    {
      key: "normal",
      shortLabel: "P",
      label: t("canvas.blocks.markdown.toolbar.normal"),
      isActive: currentTextStyle === "normal",
      onClick: () => setTextStyle("normal"),
    },
    {
      key: "heading1",
      shortLabel: "H1",
      label: t("canvas.blocks.markdown.toolbar.heading1"),
      isActive: currentTextStyle === "heading1",
      onClick: () => setTextStyle("heading1"),
    },
    {
      key: "heading2",
      shortLabel: "H2",
      label: t("canvas.blocks.markdown.toolbar.heading2"),
      isActive: currentTextStyle === "heading2",
      onClick: () => setTextStyle("heading2"),
    },
    {
      key: "heading3",
      shortLabel: "H3",
      label: t("canvas.blocks.markdown.toolbar.heading3"),
      isActive: currentTextStyle === "heading3",
      onClick: () => setTextStyle("heading3"),
    },
  ];

  const toolbarActions = [
    {
      key: "bold",
      label: `${t("canvas.blocks.markdown.toolbar.bold")} (Ctrl+B)`,
      icon: Bold,
      isActive: editor?.isActive("bold"),
      onClick: toggleBold,
    },
    {
      key: "italic",
      label: `${t("canvas.blocks.markdown.toolbar.italic")} (Ctrl+I)`,
      icon: Italic,
      isActive: editor?.isActive("italic"),
      onClick: toggleItalic,
    },
    {
      key: "bulletList",
      label: t("canvas.blocks.markdown.toolbar.bulletList"),
      icon: List,
      isActive: editor?.isActive("bulletList"),
      onClick: toggleBulletList,
    },
    {
      key: "orderedList",
      label: t("canvas.blocks.markdown.toolbar.numberedList"),
      icon: ListOrdered,
      isActive: editor?.isActive("orderedList"),
      onClick: toggleOrderedList,
    },
    {
      key: "checklist",
      label: t("canvas.blocks.markdown.toolbar.checklist"),
      icon: CheckSquare,
      isActive: editor?.isActive("taskList"),
      onClick: toggleTaskList,
    },
  ];

  // Unified render: EditorContent is always mounted to prevent NodeView remounting
  // (which causes flushSync-inside-lifecycle errors in React 18 + TipTap).
  return (
    <div
      ref={containerRef}
      className={cn("relative", !readOnly && "cursor-text")}
      onMouseMove={!readOnly ? handleMouseMove : undefined}
      onMouseLeave={!readOnly ? handleMouseLeave : undefined}
    >
      {!readOnly && editor && (
        <BubbleMenu
          ref={bubbleMenuRef}
          editor={editor}
          pluginKey={`markdown-bubble-menu-${block.id}`}
          shouldShow={shouldShowBubbleMenu}
          updateDelay={0}
          options={{
            placement: "top",
            offset: 14,
            shift: { padding: 16 },
            flip: { padding: 16 },
            onUpdate: syncBubbleMenuSide,
            onHide: () => setBubbleMenuSide("top"),
          }}
          className="z-30 group"
          data-side={bubbleMenuSide}
        >
          <div
            data-markdown-bubble-menu
            className="relative flex items-center gap-0.5 rounded-xl border border-white/10 bg-blueprint-core px-1.5 py-1.5 text-background shadow-xl shadow-foreground/15 before:absolute before:left-1/2 before:h-2.5 before:w-2.5 before:-translate-x-1/2 before:rotate-45 before:border-white/10 before:bg-blueprint-core before:content-[''] group-data-[side=top]:before:top-full group-data-[side=top]:before:-translate-y-1/2 group-data-[side=top]:before:border-b group-data-[side=top]:before:border-r group-data-[side=bottom]:before:bottom-full group-data-[side=bottom]:before:translate-y-1/2 group-data-[side=bottom]:before:border-t group-data-[side=bottom]:before:border-l"
          >
            <div className="flex items-center gap-0.5 rounded-lg bg-background/6 p-0.5">
              {textStyleActions.map((action) => (
                <FloatingToolbarButton
                  key={action.key}
                  label={action.label}
                  isActive={action.isActive}
                  onClick={action.onClick}
                  variant="text"
                >
                  {action.shortLabel}
                </FloatingToolbarButton>
              ))}
            </div>

            <div className="mx-1 h-5 w-px bg-background/10" />

            {toolbarActions.map((action) => (
              <FloatingToolbarButton
                key={action.key}
                label={action.label}
                isActive={Boolean(action.isActive)}
                onClick={action.onClick}
              >
                <action.icon className="h-4 w-4" />
              </FloatingToolbarButton>
            ))}
          </div>
        </BubbleMenu>
      )}

      <div className="markdown-editor text-ui-body">
        <EditorContent editor={editor} />
      </div>

      {/* Row color overlay — shown when hovering a table row inside the markdown editor */}
      {!readOnly && hoveredRow !== null && hoveredTable && rowRect && (
        <>
          {/* Color circle — positioned left of the table with room for the trash button */}
          <button
            className="absolute z-20 flex items-center justify-center w-6 h-6 rounded-full bg-background shadow text-muted-foreground hover:text-foreground transition-colors outline-none"
            style={{
              left: tableLeft - 60,
              top: rowRect.top + rowRect.height / 2 - 12,
            }}
            aria-label={t("canvas.blocks.table.rowColor", "Row color")}
            title={t("canvas.blocks.table.rowColor", "Row color")}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => setColorPickerOpen((v) => !v)}
          >
            <Paintbrush
              className="h-3 w-3"
              style={
                activeRowColor
                  ? { color: colorSwatches.find((s) => s.value === activeRowColor)?.bg }
                  : undefined
              }
            />
          </button>

          {/* Delete row button — shown when table has more than one row */}
          {hoveredTable && hoveredTable.querySelectorAll("tr").length > 1 && (
            <button
              className="absolute z-20 flex items-center justify-center w-6 h-6 rounded-full bg-background shadow text-muted-foreground hover:text-destructive transition-colors outline-none"
              style={{
                left: tableLeft - 32,
                top: rowRect.top + rowRect.height / 2 - 12,
              }}
              aria-label={t("canvas.blocks.table.deleteRow", "Delete row")}
              title={t("canvas.blocks.table.deleteRow", "Delete row")}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => handleDeleteRow(hoveredRow, hoveredTable)}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}

          {/* Inline color picker */}
          {colorPickerOpen && (
            <div
              className="absolute z-30 flex items-center gap-1 p-1.5 rounded-lg bg-background border shadow-md"
              style={{
                left: tableLeft - 60,
                top: rowRect.top + rowRect.height / 2 + 16,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={cancelHoverClear}
            >
              {colorSwatches.map(({ value, bg, label }) => (
                <button
                  key={String(value)}
                  className={cn(
                    "w-5 h-5 rounded-full border-2 transition-all outline-none",
                    activeRowColor === value
                      ? "border-foreground scale-110"
                      : "border-transparent hover:border-muted-foreground/40 hover:scale-105",
                    value === null && "bg-background border-border",
                  )}
                  style={value ? { backgroundColor: bg } : {}}
                  title={label}
                  aria-label={label}
                  onClick={() => handleSetRowColor(hoveredRow, hoveredTable, value)}
                >
                  {value === null && (
                    <span className="block w-3 h-px bg-muted-foreground/40 rotate-45 mx-auto" />
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface FloatingToolbarButtonProps {
  children: ReactNode;
  isActive?: boolean;
  label: string;
  onClick: () => void;
  variant?: "icon" | "text";
}

function FloatingToolbarButton({
  children,
  isActive = false,
  label,
  onClick,
  variant = "icon",
}: FloatingToolbarButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "flex h-8 items-center justify-center rounded-md text-background/70 transition-colors hover:bg-background/10 hover:text-background",
        variant === "text" ? "min-w-9 px-2 font-tektur text-[13px] font-semibold" : "w-8",
        isActive && "bg-background/14 text-background",
      )}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}
